export const EMPANADA_PRESENTATION_SELECTION_KEY = "empanada-presentation";
export const EMPANADA_COOKING_SELECTION_KEY = "empanada-cooking";

export const DEFAULT_EMPANADA_SALE_OPTIONS = [
  { id: "unidad", label: "Unidad", unitCount: 1 },
  { id: "media-docena", label: "Media docena", unitCount: 6 },
  { id: "docena", label: "Docena", unitCount: 12 },
];

export const DEFAULT_EMPANADA_COOKING_OPTIONS = [
  { id: "horno", label: "Al horno" },
  { id: "frita", label: "Frita" },
];

const normalizeText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

const normalizeEntityId = (value) => String(value || "").trim();

const toSafeMoney = (value, fallback = 0) => {
  const parsed = Number(String(value ?? "").replace(",", "."));
  return Number.isFinite(parsed) ? Math.max(0, Number(parsed.toFixed(2))) : fallback;
};

const buildEmpanadaSaleOptionId = (label, index = 0) => {
  const normalized = normalizeText(label)
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return normalized || `empanada-sale-${index + 1}`;
};

const buildEmpanadaCookingOptionId = (label, index = 0) => {
  const normalized = normalizeText(label)
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return normalized || `empanada-cooking-${index + 1}`;
};

const getRawEmpanadaSaleOptions = (product) => {
  const options =
    product?.empanadaSaleOptions ||
    product?.empanada_sale_options ||
    product?.empanadaOptions ||
    product?.empanada_options ||
    [];

  return Array.isArray(options) ? options : [];
};

const getRawEmpanadaCookingOptions = (product) => {
  const options =
    product?.empanadaCookingOptions ||
    product?.empanada_cooking_options ||
    product?.empanadaCookOptions ||
    product?.empanada_cook_options ||
    [];

  return Array.isArray(options) ? options : [];
};

const getProductCategoryId = (product) =>
  normalizeEntityId(product?.categoryId || product?.category_id || product?.categoria);

const getProductSubcategoryId = (product) =>
  normalizeEntityId(
    product?.subcategoryId ||
      product?.subcategory_id ||
      product?.subcategoriaId ||
      product?.subcategoria_id
  );

export const getEmpanadaProductName = (product, fallback = "Empanada") =>
  String(product?.name || product?.nombre || product?.productName || fallback)
    .trim()
    .replace(/\s+/g, " ") || fallback;

export const isReservedEmpanadaCategoryName = (value) => {
  const normalized = normalizeText(value);
  const words = normalized.split(/[^a-z0-9]+/g).filter(Boolean);

  return words.some((word) => word === "empanada" || word === "empanadas");
};

export const getProductCategoryName = (product, categories = []) => {
  const directName =
    product?.categoryName ||
    product?.categoriaNombre ||
    product?.category_name ||
    product?.category ||
    "";

  if (directName) {
    return String(directName).trim();
  }

  const categoryId = getProductCategoryId(product);
  const match = (Array.isArray(categories) ? categories : []).find(
    (category) => normalizeEntityId(category?.id) === categoryId
  );

  return String(match?.name || match?.nombre || "").trim();
};

export const getProductSubcategoryName = (product, subcategories = []) => {
  const directName =
    product?.subcategoryName ||
    product?.subcategoriaNombre ||
    product?.subcategory_name ||
    product?.subcategoria ||
    "";

  if (directName) {
    return String(directName).trim();
  }

  const subcategoryId = getProductSubcategoryId(product);
  const match = (Array.isArray(subcategories) ? subcategories : []).find(
    (subcategory) => normalizeEntityId(subcategory?.id) === subcategoryId
  );

  return String(match?.name || match?.nombre || "").trim();
};

export const isEmpanadaCategoryProduct = (
  product,
  categories = [],
  subcategories = []
) =>
  isReservedEmpanadaCategoryName(getProductCategoryName(product, categories)) ||
  isReservedEmpanadaCategoryName(
    getProductSubcategoryName(product, subcategories)
  );

export const normalizeEmpanadaSaleOption = (option = {}, index = 0) => {
  const rawLabel =
    option.label ||
    option.name ||
    option.nombre ||
    option.presentationLabel ||
    option.presentation_label ||
    "";
  const optionId = normalizeEntityId(
    option.id || option.optionId || option.presentationId
  );
  const defaultOption =
    DEFAULT_EMPANADA_SALE_OPTIONS.find(
      (preset) =>
        (optionId && preset.id === optionId) ||
        (rawLabel && normalizeText(preset.label) === normalizeText(rawLabel))
    ) ||
    DEFAULT_EMPANADA_SALE_OPTIONS[index] ||
    {};
  const label =
    String(
      rawLabel || defaultOption.label || ""
    )
      .trim()
      .replace(/\s+/g, " ") || `Presentacion ${index + 1}`;
  const unitCount = Math.max(
    1,
    Math.floor(
      Number(
        option.unitCount ??
          option.unit_count ??
          option.units ??
          option.quantity ??
          option.cantidad ??
          defaultOption.unitCount ??
          1
      ) || 1
    )
  );
  const price = toSafeMoney(
    option.price ??
      option.precio ??
      option.amount ??
      option.fullPrice ??
      option.full_price
  );

  return {
    id:
      optionId ||
      defaultOption.id ||
      buildEmpanadaSaleOptionId(label, index),
    label,
    unitCount,
    price,
    active: option.active !== false && option.enabled !== false,
  };
};

export const normalizeEmpanadaSaleOptions = (
  options = [],
  { includeInactive = false } = {}
) => {
  const seen = new Set();

  return (Array.isArray(options) ? options : [])
    .map(normalizeEmpanadaSaleOption)
    .filter((option) => includeInactive || option.active !== false)
    .filter((option) => {
      const key = normalizeEntityId(option.id) || buildEmpanadaSaleOptionId(option.label);

      if (!key || seen.has(key)) {
        return false;
      }

      seen.add(key);
      return true;
    });
};

export const normalizeEmpanadaCookingOption = (option = {}, index = 0) => {
  const rawLabel =
    option.label ||
    option.name ||
    option.nombre ||
    option.cookingLabel ||
    option.cooking_label ||
    "";
  const optionId = normalizeEntityId(
    option.id || option.optionId || option.cookingId || option.cooking_id
  );
  const defaultOption =
    DEFAULT_EMPANADA_COOKING_OPTIONS.find(
      (preset) =>
        (optionId && preset.id === optionId) ||
        (rawLabel && normalizeText(preset.label) === normalizeText(rawLabel))
    ) ||
    DEFAULT_EMPANADA_COOKING_OPTIONS[index] ||
    {};
  const label =
    String(rawLabel || defaultOption.label || "")
      .trim()
      .replace(/\s+/g, " ") || `Coccion ${index + 1}`;

  return {
    id:
      optionId ||
      defaultOption.id ||
      buildEmpanadaCookingOptionId(label, index),
    label,
    active: option.active !== false && option.enabled !== false,
  };
};

export const normalizeEmpanadaCookingOptions = (
  options = [],
  { includeInactive = false } = {}
) => {
  const seen = new Set();

  return (Array.isArray(options) ? options : [])
    .map(normalizeEmpanadaCookingOption)
    .filter((option) => includeInactive || option.active !== false)
    .filter((option) => {
      const key =
        normalizeEntityId(option.id) || buildEmpanadaCookingOptionId(option.label);

      if (!key || seen.has(key)) {
        return false;
      }

      seen.add(key);
      return true;
    });
};

export const getEmpanadaSaleOptions = (product) =>
  normalizeEmpanadaSaleOptions(getRawEmpanadaSaleOptions(product));

export const productHasEmpanadaSaleOptions = (product) =>
  getEmpanadaSaleOptions(product).length > 0;

export const getEmpanadaCookingOptions = (product) =>
  normalizeEmpanadaCookingOptions(getRawEmpanadaCookingOptions(product));

export const productHasEmpanadaCookingOptions = (product) =>
  getEmpanadaCookingOptions(product).length > 0;

export const findEmpanadaSaleOption = (product, saleOptionId = "") => {
  const options = getEmpanadaSaleOptions(product);
  const safeSaleOptionId = normalizeEntityId(saleOptionId);

  if (!options.length) {
    return null;
  }

  if (!safeSaleOptionId) {
    return options[0];
  }

  const normalizedSaleKey = normalizeText(safeSaleOptionId);

  return (
    options.find(
      (option) =>
        option.id === safeSaleOptionId ||
        normalizeText(option.label) === normalizedSaleKey
    ) || options[0]
  );
};

export const findEmpanadaCookingOption = (product, cookingOptionId = "") => {
  const options = getEmpanadaCookingOptions(product);
  const safeCookingOptionId = normalizeEntityId(cookingOptionId);

  if (!options.length) {
    return null;
  }

  if (!safeCookingOptionId) {
    return options[0];
  }

  const normalizedCookingKey = normalizeText(safeCookingOptionId);

  return (
    options.find(
      (option) =>
        option.id === safeCookingOptionId ||
        normalizeText(option.label) === normalizedCookingKey
    ) || options[0]
  );
};

export const resolveEmpanadaProductPrice = (
  product,
  { saleOptionId = "", saleOption = null } = {}
) => {
  if (!product) {
    return 0;
  }

  const resolvedSaleOption =
    saleOption || findEmpanadaSaleOption(product, saleOptionId);

  if (resolvedSaleOption) {
    return toSafeMoney(resolvedSaleOption.price);
  }

  if (product.price !== undefined && product.price !== null && product.price !== "") {
    return Number(product.price || 0);
  }

  if (product.precio !== undefined && product.precio !== null && product.precio !== "") {
    return Number(product.precio || 0);
  }

  return Number(product.suggestedPrice || product.precioSugerido || 0);
};

export const createEmpanadaSaleSelectionDetail = (saleOption) => {
  if (!saleOption?.label) {
    return "";
  }

  const unitCount = Number(saleOption.unitCount || 0);
  const unitLabel =
    unitCount > 1 ? `${unitCount} unidades` : "1 unidad";

  return `Presentacion: ${saleOption.label} (${unitLabel})`;
};

export const createEmpanadaCookingSelectionDetail = (cookingOption) => {
  if (!cookingOption?.label) {
    return "";
  }

  return `Coccion: ${cookingOption.label}`;
};
